import { isOrgAdminRole, ORG_ROLE_LABELS } from "@repo/shared";
import { useNavigate } from "@tanstack/react-router";
import { useAuth } from "@workos-inc/authkit-react";
import { ChevronDown, LogOut, Settings, UserPlus } from "lucide-react";
import { ShieldCogIcon } from "@/components/shield-cog-icon";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { LongLabelTooltip } from "@/components/ui/long-label-tooltip";
import { useOrganization } from "@/contexts/organization-context";
import { useTabsActions } from "@/contexts/tabs-context";
import { useCurrentUser } from "@/hooks/use-current-user";
import { cn } from "@/lib/utils";

export function OrgDropdown() {
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const { openAdminTab } = useTabsActions();
  const { organization, allOrganizations, switchOrganization } = useOrganization();
  const currentUser = useCurrentUser();
  const isPlatformAdmin = currentUser?.isPlatformAdmin === true;

  const orgName = organization?.name ?? "Organization";
  const orgInitial = orgName[0]?.toUpperCase() ?? "O";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        data-testid="org-dropdown-trigger"
        data-workos-org-id={organization?.workosOrgId}
        className="flex min-w-0 items-center gap-2 p-1.5 -m-1.5 transition-colors outline-none hover:bg-muted/50 focus-visible:bg-muted/50 focus-visible:ring-2 focus-visible:ring-ring select-none"
      >
        <div
          data-testid="org-dropdown-initial"
          className="flex size-7 shrink-0 items-center justify-center bg-primary text-[13px] font-bold text-primary-foreground select-none"
        >
          {orgInitial}
        </div>
        <LongLabelTooltip
          content={orgName}
          side="bottom"
          align="start"
          contentClassName="max-w-80 whitespace-normal break-words"
        >
          <span
            data-testid="org-dropdown-name"
            className="min-w-0 truncate select-none text-left text-sm font-semibold"
          >
            {orgName}
          </span>
        </LongLabelTooltip>
        <ChevronDown className="size-3.5 text-neutral-dim shrink-0" />
      </DropdownMenuTrigger>

      <DropdownMenuContent align="start" side="bottom" alignOffset={-2} className="w-[240px]">
        {/* Organization switcher */}
        {allOrganizations && allOrganizations.length > 0 && (
          <>
            <DropdownMenuGroup>
              <DropdownMenuLabel className="text-[11px] text-neutral-dim font-medium px-3 pb-1">
                Organizations
              </DropdownMenuLabel>
              {allOrganizations.map((org) => {
                const isActive = org._id === organization?._id;
                return (
                  <DropdownMenuItem
                    key={org._id}
                    data-testid="org-dropdown-option"
                    data-workos-org-id={org.workosOrgId}
                    onClick={() => switchOrganization(org._id)}
                    className={cn(
                      "gap-2 h-8 px-2",
                      isActive && "bg-muted border-l-2 border-primary"
                    )}
                  >
                    <div className="flex size-[22px] shrink-0 items-center justify-center bg-primary text-[10px] font-bold !text-primary-foreground">
                      {org.name[0]?.toUpperCase() ?? "O"}
                    </div>
                    <LongLabelTooltip
                      content={org.name}
                      side="right"
                      align="start"
                      contentClassName="max-w-80 whitespace-normal break-words"
                    >
                      <span className="min-w-0 flex-1 truncate text-xs font-medium">
                        {org.name}
                      </span>
                    </LongLabelTooltip>
                    {isOrgAdminRole(org.role) && (
                      <span className="text-[10px] text-neutral-dim font-medium lowercase">
                        {ORG_ROLE_LABELS.app_org_admin}
                      </span>
                    )}
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
          </>
        )}

        {/* Actions */}
        <DropdownMenuGroup>
          <DropdownMenuItem
            onClick={() => navigate({ to: "/settings/members-roles" })}
            className="gap-2 h-8"
          >
            <UserPlus className="size-3.5 text-neutral-dim" />
            Invite your team
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => navigate({ to: "/settings/general" })}
            className="gap-2 h-8"
          >
            <Settings className="size-3.5 text-neutral-dim" />
            Organization Settings
          </DropdownMenuItem>
          {isPlatformAdmin && (
            <DropdownMenuItem onClick={openAdminTab} className="gap-2 h-8">
              <ShieldCogIcon className="size-3.5 text-neutral-dim" />
              Admin Portal
            </DropdownMenuItem>
          )}
        </DropdownMenuGroup>
        <DropdownMenuSeparator />

        {/* Sign out */}
        <DropdownMenuGroup>
          <DropdownMenuItem
            onClick={() => signOut({ returnTo: window.location.origin })}
            className="gap-2 h-8"
          >
            <LogOut className="size-3.5 text-neutral-dim" />
            Sign out
          </DropdownMenuItem>
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
